/**
 * @license CKCode
 * Copyright 2023 CourseKata
 * SPDX-License-Identifier: GPL-3.0-or-later
 */
import { __decorate } from "tslib";
import "@material/web/icon/icon.js";
import { MathJaxTypesetter } from "@jupyterlab/mathjax-extension";
import { MimeModel, standardRendererFactories } from "@jupyterlab/rendermime";
import { LitElement, html } from "lit";
import { property } from "lit/decorators.js";
import { NamedLogger } from "../../core/logging.js";
import { renderMarkdown, sanitizer } from "../../utils.js";
const log = new NamedLogger("ckcode");
export class MarkdownMessageArea extends LitElement {
    /** Clear the message area. */
    clear() {
        this.message = this.icon = this.type = undefined;
    }
    render() {
        if (!this.message)
            return;
        this.type = this.normalizeType(this.type);
        this.icon = this.resolveIcon(this.type, this.icon);
        return html `
      <main tabindex="0" class="message message--${this.type}">
        ${this.renderIcon()} ${this.renderContent()}
      </main>
    `;
    }
    /**
     * Normalizes the message type to one of the four valid types.
     * @param type the requested type of message to display
     * @returns the normalized type
     */
    normalizeType(type) {
        type = type?.toLowerCase();
        if (!["info", "success", "warning", "error"].includes(type)) {
            log.warn(`Unknown message type: ${type}, defaulting to "info".`);
            type = "info";
        }
        return type;
    }
    /**
     * Resolve the icon to use based on the message type (or use user's choice).
     * @param icon the user's choice of icon, if any
     * @param type the type of message to display
     * @returns the icon to show, if any
     */
    resolveIcon(type, icon) {
        icon = icon?.toLowerCase();
        if (!icon) {
            switch (type) {
                case "info":
                    return "info";
                case "success":
                    return "check_circle";
                case "warning":
                    return "warning";
                case "error":
                    return "error";
            }
        }
        return icon;
    }
    /** Render the message type indicator. */
    renderIcon() {
        if (!this.type || !this.icon)
            return;
        return html `<div class="indicator" aria-hidden="true">
      <md-icon title="${this.type}">${this.icon}</md-icon>
    </div>`;
    }
    /** Render the message content. */
    renderContent() {
        return html `<div class="content">${this.renderMarkdown(this.message)}</div>`;
    }
    renderMarkdown(content) {
        // return unsafeHTML(sanitizer.sanitize(renderMarkdown(this.message ?? ""))) as LitRenderable;
        const renderedText = this.renderAs("application/vnd.jupyter.stderr", content);
        content = renderedText.node.textContent ?? "";
        const renderedMarkdown = this.renderAs("text/markdown", content);
        return renderedMarkdown.node;
    }
    renderAs(mimeToRenderAs, content) {
        const rendererFactory = standardRendererFactories.find((factory) => factory.mimeTypes.find((mimeType) => mimeType === mimeToRenderAs));
        // render the output
        const renderer = rendererFactory.createRenderer({
            mimeType: mimeToRenderAs,
            sanitizer: sanitizer,
            latexTypesetter: new MathJaxTypesetter(),
            markdownParser: new MarkdownParser(),
            linkHandler: null,
            resolver: null,
        });
        const mimeModel = new MimeModel({ data: { [mimeToRenderAs]: content }, trusted: true });
        renderer.renderModel(mimeModel);
        return renderer;
    }
}
__decorate([
    property({ type: String })
], MarkdownMessageArea.prototype, "message", void 0);
__decorate([
    property({ type: String })
], MarkdownMessageArea.prototype, "icon", void 0);
__decorate([
    property({ type: String })
], MarkdownMessageArea.prototype, "type", void 0);
/** Wrapper class implementing the {@linkcode IRenderMime.IMarkdownParser} with marked. */
class MarkdownParser {
    async render(source) {
        return renderMarkdown(source);
    }
}
//# sourceMappingURL=markdown-message-area.js.map